/**
 * Where every node of a `<TreeChart>` sits.
 *
 * **A node is drawn in its own lane. Always.** Not its parent's, not one past its parent's.
 * A record that does not belong to a rank must not appear under that rank's heading, and no
 * arrangement of the tree is worth lying about which rank a record has.
 *
 * A child at its parent's own rank, or before it, therefore lands level with its parent or
 * behind it. That is a real thing the data says, and the chart says it back. It was solved
 * once by **pushing** such a child one lane past its parent — which put a Division card under
 * the National heading, and made the chart unable to answer the one question it exists to
 * answer. The connector carries that weight instead: a same-lane run leaves the parent's
 * TRAILING edge and returns to the child's, so it can never be mistaken for the run that
 * arrives from the lane before. Geometry bends; the rank does not.
 *
 * Two axes, named for what they do rather than for x and y, because the orientation swaps
 * them:
 *
 * - **main** — the way the tree grows. Set by the node's lane, so every record of a rank
 *   lines up.
 * - **cross** — the way siblings spread. Set by a tidy-tree pass: a parent centres on its
 *   children, and a leaf takes the next free slot.
 */

export interface LayoutNode<T> {
  key: string;
  node: T;
  /** Index into the lane list. The node's own rank, never adjusted. */
  lane: number;
  /**
   * How deep inside its own lane this node sits — its **sub-tier**.
   *
   * Zero for a node whose parent is in another lane. One more than its parent for a node at
   * its parent’s own rank, which is what gives a same-rank child a channel of its own inside
   * the tier instead of a position level with the parent it hangs from.
   */
  depth: number;
  /** Position along the axis siblings spread on, in slots. Fractional when centred. */
  slot: number;
  /** The key of the node this one hangs from, or null for a root. */
  parentKey: string | null;
  hasChildren: boolean;
  expanded: boolean;
}

export interface TreeLayout<T> {
  placed: LayoutNode<T>[];
  byKey: Map<string, LayoutNode<T>>;
  /** How thick each lane is along the main axis, in px, before its gutter. */
  laneSpans: number[];
  /** Where each lane starts along the main axis, in px. */
  laneOffsets: number[];
  /** How many columns the chart draws — exactly one per rank in the ladder. */
  lanes: number;
  /** The last slot used, so the caller can size the cross axis. */
  slots: number;
}

export interface LayoutOptions<T> {
  getKey: (node: T) => string;
  getChildren: (node: T) => readonly T[];
  /** The node's lane index, already resolved against the ladder. */
  laneOf: (node: T) => number;
  /** How many lanes the ladder declares. Columns are drawn for all of them, occupied or not. */
  laneCount: number;
  /** The main-axis size of one card. */
  cardMain: number;
  /** The space between one lane and the next. */
  gutter: number;
  /**
   * How far each sub-tier is inset inside its lane, along the main axis.
   *
   * The lane GROWS by it rather than the card shrinking by it. Taking it out of the card
   * looks tidier — every card in a tier then ends on one edge — but the card is only
   * `cardMain` long to begin with, and top-to-bottom that is the height of one row: two
   * sub-tiers deep and there is no card left. A band that grows can always hold one more.
   */
  subIndent: number;
  collapsed: ReadonlySet<string>;
  collapsible: boolean;
}

export function computeLayout<T>(nodes: readonly T[], options: LayoutOptions<T>): TreeLayout<T> {
  const { getKey, getChildren, laneOf, laneCount, cardMain, gutter, subIndent, collapsed, collapsible } =
    options;

  const placed: LayoutNode<T>[] = [];
  const byKey = new Map<string, LayoutNode<T>>();
  /** The deepest sub-tier found in each lane, which is what makes a lane thicker than a card. */
  const deepest = new Array<number>(Math.max(laneCount, 1)).fill(0);

  let nextSlot = 0;

  /**
   * Places one node and everything under it, and reports the slot it took.
   *
   * Children are split by where they LAND rather than by what they are, and that split is
   * what keeps two cards off one another now that nothing is pushed:
   *
   * - a child in a **later** lane is in another column, so the parent can centre on it
   *   freely — that centring is what makes a chart read as a chart;
   * - a child in the parent's **own lane or an earlier one** shares a column with it, so
   *   centring on it would put the two cards in the same place. It takes the next free slot
   *   after the parent's whole subtree instead.
   *
   * Centring on every child is what the push used to make safe, by guaranteeing no child was
   * ever in its parent's column. Without the push that guarantee is gone and this split is
   * what replaces it.
   */
  function place(
    node: T,
    parentKey: string | null,
    parentLane: number,
    parentDepth: number,
  ): number {
    const key = getKey(node);
    // The node's own rank, and nothing about its parent enters into it.
    const lane = ownLane(laneOf(node), laneCount);

    /*
      A child at its parent’s OWN rank opens a sub-tier inside that rank. A child at an
      earlier rank does not: it is back in a column of its own, at the head of it.
    */
    const depth =
      parentKey !== null && lane === parentLane ? Math.min(parentDepth + 1, MAX_DEPTH) : 0;

    deepest[lane] = Math.max(deepest[lane], depth);

    const children = getChildren(node);
    const expanded = !collapsible || !collapsed.has(key);
    const shown = expanded ? children : [];

    const advancing = shown.filter((child) => ownLane(laneOf(child), laneCount) > lane);
    const level = shown.filter((child) => ownLane(laneOf(child), laneCount) <= lane);

    let slot: number;

    if (advancing.length > 0) {
      const taken = advancing.map((child) => place(child, key, lane, depth));
      slot = (Math.min(...taken) + Math.max(...taken)) / 2;
    } else {
      slot = nextSlot;
      nextSlot += 1;
    }

    const entry: LayoutNode<T> = {
      key,
      node,
      lane,
      depth,
      slot,
      parentKey,
      hasChildren: children.length > 0,
      expanded,
    };

    placed.push(entry);
    byKey.set(key, entry);

    /*
      After the parent is placed, so each takes a slot past everything already laid down —
      including the parent itself, which is the collision this ordering exists to avoid.
    */
    for (const child of level) {
      place(child, key, lane, depth);
    }

    return slot;
  }

  for (const root of nodes) {
    place(root, null, -1, 0);
  }

  // One column per rank the ladder declares, occupied or not — an empty one is a skipped rank.
  const lanes = Math.max(laneCount, 1);
  /*
    A lane is one card thick plus room for however many sub-tiers it turned out to hold, so a
    tier with no same-rank children is exactly as wide as it always was.
  */
  const laneSpans = Array.from(
    { length: lanes },
    (_, lane) => cardMain + (deepest[lane] ?? 0) * subIndent,
  );

  const laneOffsets: number[] = [];
  /*
    Half a gutter of clearance before the first lane, so its cards do not sit flush against
    the panel’s own edge. Every later lane already has it — the gutter between two lanes puts
    half of itself on each side of the rule — and starting at zero made lane 0 the one column
    whose cards touched a border. It also makes every column band exactly `span + gutter`.
  */
  let running = gutter / 2;

  for (const span of laneSpans) {
    laneOffsets.push(running);
    running += span + gutter;
  }

  return { placed, byKey, laneSpans, laneOffsets, lanes, slots: Math.max(nextSlot, 1) };
}

/**
 * A lane index that is a real column.
 *
 * A rank the ladder does not carry resolves to the first, and one past its end to the last —
 * clamping is the only adjustment made anywhere, and it exists so a node can never be placed
 * where no column is drawn.
 */
function ownLane(lane: number, laneCount: number): number {
  if (!Number.isFinite(lane) || lane < 0) {
    return 0;
  }

  return Math.min(lane, Math.max(laneCount - 1, 0));
}

/**
 * How many sub-tiers deep a lane will inset before it stops.
 *
 * The inset is taken out of the card, so an uncapped chain would eventually leave no card at
 * all. Past this depth the cards stop shrinking and simply share a channel.
 */
const MAX_DEPTH = 3;

/** Where a node's card starts along the axis the tree grows — its lane, plus its sub-tier. */
export function mainStart<T>(
  entry: LayoutNode<T>,
  layout: TreeLayout<T>,
  subIndent: number,
): number {
  return (layout.laneOffsets[entry.lane] ?? 0) + entry.depth * subIndent;
}


